import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Phone } from 'lucide-react';

const ProjectsPage = () => {
  const projects = [
    {
      image: 'https://images.unsplash.com/photo-1558244380-3d44c532012d?q=80&w=800&auto=format&fit=crop',
      title: 'Backyard Landscape Design',
      location: 'Seattle, WA',
      category: 'Landscape Design',
      description: 'Complete backyard transformation featuring native plantings, stone pathways, and a custom patio area.',
    },
    {
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800&auto=format&fit=crop',
      title: 'Patio Installation',
      location: 'Bellevue, WA',
      category: 'Hardscaping',
      description: 'Modern paver patio with built-in fire pit and surrounding landscape beds.',
    },
    {
      image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop',
      title: 'Residential Lawn Installation',
      location: 'Kirkland, WA',
      category: 'Lawn Care',
      description: 'Full lawn renovation including sod installation, irrigation system, and border plantings.',
    },
    {
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=800&auto=format&fit=crop',
      title: 'Garden Irrigation System',
      location: 'Redmond, WA',
      category: 'Irrigation',
      description: 'Efficient drip irrigation system installed for extensive garden beds.',
    },
    {
      image: 'https://images.unsplash.com/photo-1509423350716-97f9360b4e09?q=80&w=800&auto=format&fit=crop',
      title: 'Seasonal Cleanup & Maintenance',
      location: 'Seattle, WA',
      category: 'Maintenance',
      description: 'Comprehensive fall cleanup and winter preparation for a large residential property.',
    },
    {
      image: 'https://images.unsplash.com/photo-1598902108854-10e335adac99?q=80&w=800&auto=format&fit=crop',
      title: 'Tree & Shrub Care',
      location: 'Mercer Island, WA',
      category: 'Tree Care',
      description: 'Professional pruning and shaping of mature trees and ornamental shrubs.',
    },
    {
      image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?q=80&w=800&auto=format&fit=crop',
      title: 'Front Yard Renovation',
      location: 'Bothell, WA',
      category: 'Landscape Design',
      description: 'Complete front yard redesign with drought-tolerant plants and decorative rock features.',
    },
    {
      image: 'https://images.unsplash.com/photo-1584738766473-61c083514bf4?q=80&w=800&auto=format&fit=crop',
      title: 'Retaining Wall Installation',
      location: 'Issaquah, WA',
      category: 'Hardscaping',
      description: 'Natural stone retaining wall with integrated planting beds on a sloped property.',
    },
    {
      image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?q=80&w=800&auto=format&fit=crop',
      title: 'Outdoor Living Space',
      location: 'Woodinville, WA',
      category: 'Hardscaping',
      description: 'Multi-level deck and patio combination with outdoor kitchen prep area.',
    },
  ];

  const categories = ['All', 'Landscape Design', 'Hardscaping', 'Lawn Care', 'Irrigation', 'Maintenance', 'Tree Care'];

  return (
    <div className="min-h-screen pt-20" data-testid="projects-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#2E5C31]" data-testid="projects-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Poppins']">
            Our Recent Projects
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Browse through our portfolio of landscaping projects completed throughout the Seattle area.
          </p>
        </div>
      </section>

      {/* Projects Gallery */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          {/* Category Filter (Visual Only) */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category, index) => (
              <button
                key={category}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  index === 0
                    ? 'bg-[#2E5C31] text-white'
                    : 'bg-white text-[#4A4A4A] hover:bg-[#2E5C31] hover:text-white border border-gray-200'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                data-testid={`project-item-${index}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#D4E157] text-[#1A1A1A] px-3 py-1 rounded-full text-xs font-semibold">
                      {project.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-semibold font-['Poppins']">{project.title}</h3>
                    <p className="text-gray-300 text-sm">{project.location}</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[#4A4A4A] text-sm">{project.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <p className="text-center text-[#888888] text-sm mt-12 max-w-2xl mx-auto">
            Each project is unique and customized to the client's needs and property. Results may vary based on property conditions, materials selected, and project scope.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#2E5C31]" data-testid="projects-cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Poppins']">
            Let's Create Your Dream Outdoor Space
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Contact us today to discuss your landscaping vision. We'll provide a free consultation and detailed estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#estimate-form">
              <Button
                className="bg-white text-[#2E5C31] hover:bg-[#D4E157] hover:text-[#1A1A1A] rounded-full px-10 py-4 text-lg font-semibold shadow-lg transition-all duration-300"
                data-testid="projects-cta-estimate"
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18888808889">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#2E5C31] rounded-full px-10 py-4 text-lg font-semibold transition-all duration-300"
                data-testid="projects-cta-call"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Us Today
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
